import { Injectable } from '@angular/core';
import { firstValueFrom, take } from 'rxjs';
import { SelectedProjectApiService } from 'src/app/shared/components/selected-project/selected-project.api.service';
import { Catalog } from 'src/app/shared/models/echo/catalog';
import { ModelLibrary, ModelLibraryCollection } from 'src/app/shared/models/model-library';
import { Project } from 'src/app/shared/models/project';
import { GeographyApiService } from 'src/app/shared/services/echo/geography.api.service';
import { SetupLogicService } from 'src/app/shared/services/setup.logic.service';
import { Constants } from 'src/app/shared/utils/constants';
import { enrichProjectXCompoundXModels } from 'src/app/shared/utils/project-x-compound-x-model.utils';
import { EnviromentalRiskAssessmentService } from '../environmental-risk-assessment/environmental-risk-assessment.service';

export interface EraSetupValidation {
  isValidSetup: boolean;
  isValidAquaticModel: boolean;
  validSetupPlantModel: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class EraSetupHydrationService {

  private readonly aquaticCompartment = Constants.COMPARTMENTS.SURFACE_WATER.replace(' ', '').toLowerCase();
  private readonly mainGeographies = [Constants.CROP_GEOGRAPHIES.EUROPE_UNION, Constants.CROP_GEOGRAPHIES.UK, Constants.CROP_GEOGRAPHIES.USA];

  constructor(private selectedProjectApiService: SelectedProjectApiService,
    private geographyApiService: GeographyApiService,
    private setupLogicService: SetupLogicService,
    private ERAService: EnviromentalRiskAssessmentService) { }

  async hydrate(project: Project, disciplinePk: number, modelsCollection: ModelLibraryCollection[]): Promise<EraSetupValidation> {
    await this.setSelectedGeography(project);
    await this.loadProjectXCompoundXModels(project, disciplinePk, modelsCollection);

    const usedModels = this.resolveUsedModels(project, modelsCollection);
    this.ERAService.SetUsedModels(usedModels);

    const hasGeography = project.countryPk != undefined || project.regionPk != undefined;
    return {
      isValidSetup: !!project.name && !!project.formulationPk && hasGeography && usedModels.length > 0,
      isValidAquaticModel: usedModels.includes(this.aquaticCompartment),
      validSetupPlantModel: usedModels.some(model => model !== this.aquaticCompartment),
    };
  }

  private async setSelectedGeography(project: Project): Promise<void> {
    project.regionPk = project.regionPk ?? undefined;
    project.countryPk = project.countryPk ?? undefined;

    const geographies = await firstValueFrom(this.geographyApiService.getRegionCountryCatalog().pipe(take(1)));
    const geography = geographies.find((catalog: Catalog) => catalog.source === 'Region' && catalog.key === project.regionPk)
      ?? geographies.find((catalog: Catalog) => catalog.source === 'Country' && catalog.key === project.countryPk);

    if (geography) {
      project.regionPk = geography.source === 'Region' ? geography.key : undefined;
      project.countryPk = geography.source === 'Country' ? geography.key : undefined;
      if (this.mainGeographies.includes(geography.name ?? '')) project.geography = geography.name;
    }

    this.setupLogicService.selectedGeographyKey.set(geography);
  }

  private async loadProjectXCompoundXModels(project: Project, disciplinePk: number, modelsCollection: ModelLibraryCollection[]): Promise<void> {
    const models = await firstValueFrom(this.selectedProjectApiService.getProjectXCompoundXModels(project.projectPk, disciplinePk).pipe(take(1)));
    const library = modelsCollection.flatMap((collection: ModelLibraryCollection) => collection.library ?? []);
    project.projectXCompoundXModel = enrichProjectXCompoundXModels(models, library);
  }

  private resolveUsedModels(project: Project, modelsCollection: ModelLibraryCollection[]): string[] {
    const selectedModelPks = new Set((project.projectXCompoundXModel ?? []).map(model => model.ModelPk));

    const usedModels = modelsCollection.flatMap((collection: ModelLibraryCollection) => {
      const selectedModels = (collection.library ?? []).filter((model: ModelLibrary) => selectedModelPks.has(model.modelPk));
      if (!selectedModels.length) return [];
      return collection.name === this.aquaticCompartment ? [this.aquaticCompartment] : selectedModels.map((model: ModelLibrary) => model.name);
    });

    return [...new Set(usedModels)];
  }
}
